// Pure helpers for the practice-facing "WhatsApp messages" surfaces (Phase 1, 2026-10-08):
//   - My Account -> WhatsApp -> WhatsAppMessagesCard.vue (every message, with its approval status)
//   - Appointment reminders -> WhatsAppReminderTemplateNote.vue (the one message that replaces the
//     text templates at a WhatsApp-only office)
//
// Design: teamio-serverless-api/docs/plans/2026-10-08-whatsapp-message-templates-design.md
// Wire contract: GET /wa-onboarding/my-templates (conversations-service). Per template:
//   { key, title, wording, example, status, missing }
// The server never sends a template id or Meta's raw rejection text; nothing here should ask for one.
//
// Kept free of Vue and axios so cypress/node-tests can execute the REAL functions.

import { WA_TEMPLATE_KEYS } from './whatsappReminderCoverage.js';
import { TEMPLATE_VARIABLES, validateTemplateBody } from './whatsappTemplateRules.js';

/**
 * The statuses the server can send. Anything else is shown as `unknown`. `not_set_up` (Phase 3, 2026-10-11) is a
 * standard message the office has not set up yet - it only ever comes with the table's `view=all`.
 */
export const MESSAGE_STATUSES = Object.freeze(['approved', 'pending', 'not_approved', 'not_set_up', 'unknown']);

/** What the practice reads next to each message. Plain words: no Meta, no template ids. */
export const STATUS_LABELS = Object.freeze({
  approved: 'Approved',
  pending: "Waiting for WhatsApp's approval",
  not_approved: 'Not approved — Teamio is looking into it',
  not_set_up: 'Not set up yet',
  unknown: 'Status unavailable right now',
});

/**
 * Where a message is in its life, as the table reads it: `live` (sending through WhatsApp now, on or off),
 * `waiting` (set up, with WhatsApp), `not_approved`, or `none` (not set up). A server that does not send it
 * (before Phase 3) is `live`: every message it listed was live.
 */
export const SETUP_STATES = Object.freeze(['live', 'waiting', 'not_approved', 'none']);

/** The key of the appointment reminder in the served list (the exe's reminderType tag). */
export const APT_REMINDER_KEY = WA_TEMPLATE_KEYS.appointment;

/** A status string the server sent -> one of MESSAGE_STATUSES. Unrecognised or absent -> `unknown`. */
export function statusKey(status) {
  return MESSAGE_STATUSES.includes(status) ? status : 'unknown';
}

/**
 * Split message text into plain runs and [bracketed] blanks, so the blanks can be highlighted
 * WITHOUT v-html (the text is data from a server; it is only ever rendered as text).
 *
 *   splitBlanks('Hi [Practice name]!') -> [{ text: 'Hi ', blank: false },
 *                                          { text: '[Practice name]', blank: true },
 *                                          { text: '!', blank: false }]
 *
 * A bracket with nothing in it ("[]") or an unclosed one stays plain text.
 */
export function splitBlanks(text) {
  if (typeof text !== 'string' || text === '') return [];
  return text
    .split(/(\[[^\]]+\])/)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, blank: /^\[[^\]]+\]$/.test(part) }));
}

/**
 * The warning for a blank the practice has no value for (the server's `missing` list holds the
 * blank's plain label, e.g. 'Office phone'). The office phone is the known case; any other label
 * gets the general sentence so a new blank never renders as a raw label or an empty line.
 */
export function missingWarning(label) {
  const clean = typeof label === 'string' ? label.trim() : '';
  if (clean === '') return '';
  if (clean.toLowerCase() === 'office phone') {
    return "Your office phone number is missing, so patients won't see a number to call. Contact Teamio to add it.";
  }
  const sentence = clean.charAt(0).toUpperCase() + clean.slice(1);
  return `${sentence} is missing, so patients may see a gap in this message. Contact Teamio to add it.`;
}

const text = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);

/** An edit of a message that is not live (yet): waiting for WhatsApp, or not approved. */
export const PENDING_EDIT_STATUSES = Object.freeze(['pending', 'not_approved']);

/**
 * `row.pendingEdit` -> `{ status, wording }` or null. `wording` is the practice's own typed text (blanks
 * as [labels]) or null when the server could not rebuild it. Anything else is no edit at all.
 */
function normalizePendingEdit(edit) {
  if (!edit || typeof edit !== 'object' || !PENDING_EDIT_STATUSES.includes(edit.status)) return null;
  return { status: edit.status, wording: text(edit.wording) };
}

/**
 * `row.sampleValues` -> `{ [label]: string|null }` for EXACTLY the three blanks a practice may use. A
 * value that is absent, blank or not a string is null (the preview then shows the label, as the card's
 * own "What patients see" does). Nothing but those three labels ever gets through.
 */
function normalizeSampleValues(values) {
  const source = values && typeof values === 'object' ? values : {};
  return TEMPLATE_VARIABLES.reduce((out, v) => {
    const raw = source[v.label];
    return { ...out, [v.label]: typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null };
  }, {});
}

/**
 * Validate and shape the my-templates response. THROWS on anything that is not
 * `{ templates: [...] }` - a 200 with the wrong body (an HTML error page from a proxy, say) must
 * land in the card's error state, never in "No WhatsApp messages yet", which would tell a practice
 * a false thing about its own account.
 *
 * @returns {Array<{ key: string, title: string, wording: string|null, example: string|null,
 *                   status: string, missing: string[],
 *                   pendingEdit: ?{ status: string, wording: ?string },
 *                   sampleValues: object,
 *                   enabled: boolean, usedFor: string|null, setup: string }>}
 */
export function normalizeTemplates(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.templates)) {
    throw new Error('WhatsApp messages: unexpected response');
  }
  return data.templates
    .filter((t) => t && typeof t === 'object')
    .map((t) => ({
      key: typeof t.key === 'string' ? t.key : '',
      title: text(t.title) || 'WhatsApp message',
      wording: text(t.wording),
      example: text(t.example),
      status: statusKey(t.status),
      missing: Array.isArray(t.missing)
        ? t.missing.filter((m) => typeof m === 'string' && m.trim() !== '')
        : [],
      pendingEdit: normalizePendingEdit(t.pendingEdit),
      sampleValues: normalizeSampleValues(t.sampleValues),
      // Only a literal `false` is off (the server writes exactly that); a server from before the switch sent
      // no field and every message it listed was on.
      enabled: t.enabled !== false,
      usedFor: text(t.usedFor),
      setup: SETUP_STATES.includes(t.setup) ? t.setup : 'live',
      // Step 4 (2026-10-11): a message the office wrote itself. Only a literal `true` counts.
      custom: t.custom === true,
    }));
}

// -- the table (Phase 3) -------------------------------------------------------------------------

/** How many characters of a message the table row shows before "…". */
export const SNIPPET_LENGTH = 110;

/**
 * The short line a table row shows for a message: what a patient would read (the filled-in example), else the
 * wording with its [blanks]. Whitespace collapsed; cut at a word boundary with "…" when it is long; null when
 * there is nothing to show (the row then says the wording is unavailable).
 */
export function snippetOf(row, max = SNIPPET_LENGTH) {
  const source = row && (text(row.example) || text(row.wording));
  if (!source) return null;
  const flat = source.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  // A word is never cut in half (unless the first word alone is longer than the line).
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?-]+$/, '')}…`;
}

/**
 * What can be done with one row. `textsOff` = a WhatsApp-only office: it sends NOTHING as a text, so a message
 * switched off there would reach no one - its switch stays on and cannot be changed here.
 *
 *   view     any row with words to show
 *   edit     a live message with wording to start from (the server still decides)
 *   setUp    a standard message the office has not set up
 *   switch   any LIVE message, whatever WhatsApp's status says right now: a status we could not load must not lock
 *            the switch, and a message WhatsApp has paused is exactly the one to switch off (patients then get a text)
 *            - except a message the office wrote itself: it is only ever sent by staff from a conversation, never
 *            automatically, so there is nothing to switch and nothing to "set up" (it was set up when it was written)
 */
export function rowActions(row, { textsOff = false } = {}) {
  const r = row || {};
  const live = r.setup === 'live';
  const custom = r.custom === true;
  return {
    view: Boolean(text(r.wording)),
    edit: live && Boolean(text(r.wording)),
    setUp: r.setup === 'none' && !custom,
    switch: live && !custom,
    switchLocked: live && !custom && Boolean(textsOff),
    // A message of its own that WhatsApp did not approve: reopen the form with its name and text to fix and send again.
    resend: custom && r.setup === 'not_approved' && Boolean(text(r.wording)),
  };
}

/** The sentence a practice reads for a message of its own that WhatsApp did not approve (nothing is "looking into it"). */
export const CUSTOM_NOT_APPROVED_LABEL = "WhatsApp didn't approve this message";
export const CUSTOM_NOT_APPROVED_HELP = "Change the wording and send it again. Your staff can't use it until WhatsApp approves it.";

/** The status pill's words for one row: a standard message keeps STATUS_LABELS; a message of the office's own that was refused says so plainly. */
export function rowStatusLabel(row) {
  const status = statusKey(row && row.status);
  if (row && row.custom === true && status === 'not_approved') return CUSTOM_NOT_APPROVED_LABEL;
  return STATUS_LABELS[status];
}

/** The sentence under a switch, in plain words. */
export function switchHint(row, { textsOff = false } = {}) {
  if (!row || row.setup !== 'live') return '';
  if (textsOff) return 'This office sends only on WhatsApp.';
  return row.enabled ? 'Patients get this on WhatsApp.' : 'Patients get a text instead.';
}

/** The confirmation before a message starts going out on WhatsApp (set up, or switched on). */
export const CONFIRM_MOVE_TO_WHATSAPP = 'Patients will get this message on WhatsApp instead of as a text.';

export const SETUP_FALLBACK_MESSAGE = "We couldn't set this message up right now. Nothing was changed. Please try again in a few minutes.";
export const SWITCH_FALLBACK_MESSAGE = "We couldn't change this right now. Nothing was changed. Please try again in a few minutes.";

/** The POST /my-templates/setup 200 body; THROWS on anything but the agreed success (never a silent "set up"). */
export function normalizeSetupResult(data) {
  if (!data || typeof data !== 'object' || data.ok !== true || data.status !== 'pending') {
    throw new Error('WhatsApp messages: unexpected set-up response');
  }
  return { status: 'pending' };
}

/** The POST /my-templates/enabled 200 body; THROWS unless it names the key and a real boolean. */
export function normalizeSwitchResult(data) {
  if (!data || typeof data !== 'object' || data.ok !== true || typeof data.key !== 'string' || typeof data.enabled !== 'boolean') {
    throw new Error('WhatsApp messages: unexpected switch response');
  }
  return { key: data.key, enabled: data.enabled };
}

/** A failed set-up or switch -> the sentence for the practice (the server's own words when it gave a code + message). */
export function actionErrorMessage(err, fallback) {
  const { code, message } = editErrorMessage(err);
  return { code, message: code && message !== EDIT_FALLBACK_MESSAGE ? message : fallback };
}

// -- the editor (Phase 2) ------------------------------------------------------------------------

const BLANK_RE = /\[([^[\]]+)\]/g;
const normalizeLabel = (label) => String(label).trim().replace(/\s+/g, ' ').toLowerCase();
const CANONICAL_LABEL = Object.freeze(
  TEMPLATE_VARIABLES.reduce((map, v) => ({ ...map, [normalizeLabel(v.label)]: v.label }), {}),
);

/** The blanks the editor offers as buttons, in the words the practice reads. */
export const BLANK_LABELS = Object.freeze(TEMPLATE_VARIABLES.map((v) => v.label));

/**
 * "What patients see" for a message being typed: each [blank] filled with this practice's own value.
 * A blank with no value on file (an office with no phone) or one that is not an allowed blank stays as
 * its [label] - never an empty string, exactly as the server's own example does. Same text handling as
 * validateTemplateBody (line endings, trimmed) so preview and send agree; cypress/node-tests pins that
 * against the server's numbered fill.
 */
export function previewWording(typed, sampleValues) {
  if (typeof typed !== 'string') return '';
  const values = sampleValues && typeof sampleValues === 'object' ? sampleValues : {};
  return typed.replace(/\r\n?/g, '\n').trim().replace(BLANK_RE, (whole, label) => {
    const canonical = CANONICAL_LABEL[normalizeLabel(label)];
    if (!canonical) return whole;
    const value = values[canonical];
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : `[${canonical}]`;
  });
}

/**
 * Put `[label]` into `value` where the cursor is, replacing any selected text. Returns the new text and
 * where the cursor belongs afterwards (right after the blank).
 */
export function insertBlank(value, selectionStart, selectionEnd, label) {
  const current = typeof value === 'string' ? value : '';
  const clamp = (n) => Math.max(0, Math.min(current.length, Number.isInteger(n) ? n : current.length));
  const start = clamp(selectionStart);
  const end = Math.max(start, clamp(selectionEnd));
  // Keep the blank from gluing onto a word ("us.[Office phone]"): a space goes in only where one is
  // missing - never after an opening bracket or quote, never before closing punctuation.
  const before = current.slice(0, start);
  const after = current.slice(end);
  const lead = before !== '' && !/[\s([{"'“‘]$/.test(before) ? ' ' : '';
  const trail = after !== '' && !/^[\s.,;:!?)\]}"'”’]/.test(after) ? ' ' : '';
  const token = `${lead}[${label}]${trail}`;
  return { text: before + token + after, caret: start + lead.length + label.length + 2 };
}

/**
 * The editor's verdict on what is typed. `canSubmit` is false while the rules say no (the save is
 * REFUSED, never stored-and-toasted) and while nothing has changed from the message that is live - a
 * "change" that changes nothing would only spend an approval.
 *
 * @param {string} typed
 * @param {string|null} current the live wording ([labels]) the practice started from
 * @param {string} key          which message (selects message-specific warnings)
 * @param {{ custom?: boolean }} [opts] `custom`: a message the office wrote itself - it may only use the blanks
 *        CUSTOM_ALLOWED_VARIABLES (the server applies the same limit on an edit of a custom message)
 */
export function editVerdict(typed, current, key, { custom = false } = {}) {
  const validation = validateTemplateBody(typed, custom ? { key, allowedVariables: CUSTOM_ALLOWED_VARIABLES } : { key });
  const same = (a) => String(a === null || a === undefined ? '' : a).replace(/\r\n?/g, '\n').trim();
  const changed = same(typed) !== same(current);
  return {
    errors: validation.errors,
    warnings: validation.warnings,
    changed,
    canSubmit: validation.ok && changed,
  };
}

export const EDIT_FALLBACK_MESSAGE = "We couldn't send your change to WhatsApp right now. Nothing was changed. Please try again in a few minutes.";
export const EDIT_FORBIDDEN_MESSAGE = "You don't have permission to change these messages. Ask an Admin or Manager at your office.";

/**
 * A failed save -> `{ code, message }` for the practice. The server writes its refusal messages for a
 * front desk (see errors.js: they are shown as-is), so a code it sent with a message is trusted; a
 * gateway page, a dropped connection or a body with no message gets the plain fallback instead. A raw
 * error text from anywhere else never reaches the screen.
 */
export function editErrorMessage(err) {
  const response = err && err.response;
  const body = response && response.data && typeof response.data === 'object' ? response.data : null;
  const code = body && typeof body.code === 'string' ? body.code : null;
  const serverMessage = body && typeof body.message === 'string' && body.message.trim() !== '' ? body.message.trim() : null;
  if (code && serverMessage) return { code, message: serverMessage };
  if (response && (response.status === 401 || response.status === 403)) {
    return { code: code || 'forbidden', message: EDIT_FORBIDDEN_MESSAGE };
  }
  return { code, message: EDIT_FALLBACK_MESSAGE };
}

/** Shape the POST's 200 body; THROWS on anything that is not the agreed success (never a silent "sent"). */
export function normalizeEditResult(data) {
  if (!data || typeof data !== 'object' || data.ok !== true || data.status !== 'pending') {
    throw new Error('WhatsApp messages: unexpected save response');
  }
  const warnings = Array.isArray(data.warnings)
    ? data.warnings.filter((w) => w && typeof w === 'object' && typeof w.message === 'string')
      .map((w) => ({ code: typeof w.code === 'string' ? w.code : '', message: w.message }))
    : [];
  return { status: 'pending', warnings };
}

// -- messages the office writes itself (Step 4, 2026-10-11) ---------------------------------------
//
// Server twin: conversations-service lib/waOnboarding/customMessages.js (the cap, the name length, the two blanks).
// The server owns every rule; these only give instant feedback so a message that can never be accepted is never sent.

/** How many messages of its own an office may have (waiting for WhatsApp, or approved). */
export const MAX_CUSTOM_MESSAGES = 10;

/** The name's length limit; the name is what staff see when they pick the message. */
export const MAX_CUSTOM_NAME_CHARS = 60;

/** The only blanks a custom message may use: the practice's name and office phone. */
export const CUSTOM_ALLOWED_VARIABLES = Object.freeze(['site_name', 'site_officephone']);

/** The blank buttons the custom-message form offers, in the words the practice reads. */
export const CUSTOM_BLANK_LABELS = Object.freeze(
  TEMPLATE_VARIABLES.filter((v) => CUSTOM_ALLOWED_VARIABLES.includes(v.name)).map((v) => v.label),
);

export const CUSTOM_FALLBACK_MESSAGE = "We couldn't send your message to WhatsApp right now. Nothing was saved. Please try again in a few minutes.";

/**
 * How many of the office's own messages count against the limit: the ones live or waiting for WhatsApp. One WhatsApp
 * did not approve does not (the office can fix and resend it), which is how the server counts too.
 */
export function customMessageCount(templates) {
  if (!Array.isArray(templates)) return 0;
  return templates.filter((t) => t && t.custom === true && (t.setup === 'live' || t.setup === 'waiting')).length;
}

/** Another message of its own would be refused: the office is at the limit. */
export function customLimitReached(templates) {
  return customMessageCount(templates) >= MAX_CUSTOM_MESSAGES;
}

/**
 * The name the table will show for what was typed, exactly as the server derives it (its key is the typed name
 * lower-cased to letters, digits and hyphens, shown back with spaces and a capital): "Late fee!!" -> "Late fee".
 * Twin of customMessages.js slugFromName + displayNameFromKey (cypress/node-tests/whatsapp-custom-message-agreement.test.js
 * runs both over the same names). '' when nothing usable was typed.
 */
export function customTitlePreview(name) {
  const slug = String(name === undefined || name === null ? '' : name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  const words = slug.replace(/-/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The verdict on a NEW message of the office's own: a name and the text. `canSubmit` is false while the name is empty
 * or too long or the rules say no to the text - the save is refused, never stored and toasted.
 *
 * @returns {{ nameError: string|null, errors: Array, warnings: Array, canSubmit: boolean }}
 */
export function customVerdict(name, typed) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  let nameError = null;
  if (trimmed === '') nameError = null; // nothing typed yet is not an error to shout about; Send just stays off
  else if (trimmed.length > MAX_CUSTOM_NAME_CHARS) nameError = `Please keep the name to ${MAX_CUSTOM_NAME_CHARS} characters or fewer.`;
  else if (customTitlePreview(trimmed) === '') nameError = 'The name needs at least one letter or number.';
  const validation = validateTemplateBody(typed, { allowedVariables: CUSTOM_ALLOWED_VARIABLES });
  return {
    nameError,
    errors: validation.errors,
    warnings: validation.warnings,
    canSubmit: trimmed !== '' && nameError === null && validation.ok,
  };
}

/** The POST /my-templates/custom 200 body; THROWS on anything but the agreed success (never a silent "sent"). */
export function normalizeCustomResult(data) {
  if (!data || typeof data !== 'object' || data.ok !== true || data.status !== 'pending' || typeof data.key !== 'string' || data.key === '') {
    throw new Error('WhatsApp messages: unexpected response to a new message');
  }
  const warnings = Array.isArray(data.warnings)
    ? data.warnings.filter((w) => w && typeof w === 'object' && typeof w.message === 'string')
      .map((w) => ({ code: typeof w.code === 'string' ? w.code : '', message: w.message }))
    : [];
  return { key: data.key, status: 'pending', warnings };
}

/** A failed new message -> `{ code, message }`: the server's own words when it gave a code, else the plain fallback. */
export function customErrorMessage(err) {
  const { code, message } = editErrorMessage(err);
  return { code, message: message === EDIT_FALLBACK_MESSAGE ? CUSTOM_FALLBACK_MESSAGE : message };
}

/** The appointment reminder out of a normalized list, or null. */
export function findAppointmentReminder(templates) {
  if (!Array.isArray(templates)) return null;
  return templates.find((t) => t && t.key === APT_REMINDER_KEY) || null;
}
